import * as R from 'ramda'
import i18n from '@/locales'
import { ANALYSIS_GROUPT_TYPE_MAP } from '@Bill/constants'
import { numerify } from '@/filters'
import { currencyUnitMap } from '@/constants/currency'
import { BRAND_MAP } from '@/constants'
import { accountsFilterByOem } from '@/utils/utils'
import { getBillResourceTypeLabel } from '@Bill/utils'

export const parseGroup = (group) => {
  if (R.is(Object, group)) {
    return {
      groupType: group.group,
      groupValue: group[group.group],
    }
  }
  if (group) {
    if (group.startsWith('tag:')) {
      return {
        groupType: 'tag',
        groupValue: group.replace('tag:', ''),
      }
    }
    if (group.startsWith('project_tag:')) {
      return {
        groupType: 'project_tag',
        groupValue: group.replace('project_tag:', ''),
      }
    }
    if (group.startsWith('instance_tag:')) {
      return {
        groupType: 'instance_tag',
        groupValue: group.replace('instance_tag:', ''),
      }
    }
  }
  return {
    groupType: group,
    groupValue: '',
  }
}

export const getGroupName = (group) => {
  const { groupType, groupValue } = parseGroup(group)
  let l = ANALYSIS_GROUPT_TYPE_MAP[groupType] ? (ANALYSIS_GROUPT_TYPE_MAP[groupType].t ? i18n.t(ANALYSIS_GROUPT_TYPE_MAP[groupType].t) : ANALYSIS_GROUPT_TYPE_MAP[groupType].value) : i18n.t(`bill.${groupType}`)
  if (['tag', 'project_tag', 'instance_tag'].includes(groupType) && groupValue) {
    l += `=${groupValue.replace('user:', '')}`
  }
  return l
}

export const getAlarmGroupsTableColumn = () => {
  return {
    field: 'alarm_groups',
    title: i18n.t('bill.text_297'),
    slots: {
      default: ({ row }, h) => {
        const { alarm_groups = [] } = row
        const ret = []
        alarm_groups.forEach(item => {
          ret.push(<div>{getGroupName(item)}</div>)
        })
        return [<div>{...ret}</div>]
      },
    },
    formatter: ({ row }) => {
      return row.alarm_groups.map(item => getGroupName(item)).join(', ')
    },
  }
}

export const getViewScopeTableColumn = () => {
  return {
    field: 'view_scope',
    title: i18n.t('common.text00118'),
    formatter: ({ row }) => {
      if (row.view_scope === 'system') {
        return i18n.t('bill.system_view')
      }
      if (row.view_scope === 'domain') {
        return i18n.t('bill.domain_view')
      }
      return i18n.t('bill.project_view')
    },
  }
}

export const getTimeRangeTableColumn = () => {
  return {
    field: 'time_range',
    title: i18n.t('bill.text_198'),
    formatter: ({ row }) => {
      const key = (row.time_range || '').replace(' ', '_').toLowerCase()
      return i18n.te(`bill.${key}`) ? i18n.t(`bill.${key}`) : (key || '-')
    },
  }
}

export const getThresholdTableColumn = () => {
  return {
    field: 'threshold',
    title: i18n.t('bill.budget_alert_threshold'),
    formatter: ({ row }) => {
      const trendText = i18n.te(`bill.trend_${(row.trend || '').toLowerCase()}`) ? i18n.t(`bill.trend_${(row.trend || '').toLowerCase()}`) : row.trend
      let currencySign = ''
      if (row.currency && currencyUnitMap[row.currency]) {
        currencySign = currencyUnitMap[row.currency].sign || ''
      }
      if (row.alarm_abs_threshold) {
        return `${trendText} ${currencySign} ${numerify(row.alarm_abs_threshold, '0,0.00')}`
      }
      if (row.alarm_rel_threshold) {
        return `${trendText} ${row.alarm_rel_threshold} %`
      }
      return '-'
    },
  }
}

export const getNameI18n = (name, type) => {
  if (name === 'other') {
    return i18n.t('bill.other')
  }
  if (type === 'resource_type') {
    return getBillResourceTypeLabel(name)
  }
  if (type === 'charge_type') {
    return i18n.te(`billingType.${name}`) ? i18n.t(`billingType.${name}`) : name
  }
  if (type === 'platform_brand') {
    return BRAND_MAP[name] ? BRAND_MAP[name].label : name
  }
  if (type === 'account') {
    return name === 'OneStack' ? accountsFilterByOem({ data: { name } }).name || name : name
  }
  if (type === 'tag' || type === 'tags') {
    if (!name) {
      return i18n.t('bill.null_value')
    } else if (name === '___no_value__') {
      return i18n.t('common_736')
    }
  }
  if (type === 'project_tag' || type === 'project_tags') {
    if (!name) {
      return i18n.t('bill.null_value')
    } else if (name === '___no_value__') {
      return i18n.t('common_736')
    }
  }
  if (type === 'instance_tag' || type === 'instancetags') {
    if (!name) {
      return i18n.t('bill.null_value')
    } else if (name === '___no_value__') {
      return i18n.t('common_736')
    }
  }
  return name
}

export const getFilterList = (formData) => {
  const ret = []
  const keys = ['bill_type', 'platform_brand', 'account', 'linked_name', 'cloudprovider_id', 'region', 'charge_type', 'domain', 'project', 'resource_type', 'spec', 'financial_unit', 'origin_res_type', 'external_project_name', 'usage_type', 'product_type']
  keys.forEach(key => {
    const condition = formData[key + '_condition'] === 'not_equals' ? '!=' : '='
    if (R.is(Array, formData[key])) {
      if (formData[key].length) {
        ret.push({
          label: `${i18n.t(`bill.${key === 'cloudprovider_id' ? 'cloudprovider' : key}`)} ${condition} ${formData[key].map(item => getNameI18n(item, key)).join('|')}`,
        })
      }
    } else {
      if (formData[key]) {
        ret.push({
          label: `${i18n.t(`bill.${key === 'cloudprovider_id' ? 'cloudprovider' : key}`)} ${condition} ${getNameI18n(formData[key], key)}`,
        })
      }
    }
  })
  const allKeys = Object.keys(formData);
  ['tags', 'instancetags', 'project_tags'].forEach(t => {
    let label = i18n.t('bill.tag')
    if (t === 'instancetags') {
      label = i18n.t('bill.instance_tag')
    } else if (t === 'project_tags') {
      label = i18n.t('bill.project_tag')
    }
    const tagKeys = allKeys.filter(key => key.startsWith(`${t}.`) && key.endsWith('.key'))
    const tagMap = {}
    tagKeys.forEach(key => {
      tagMap[formData[key]] = tagMap[formData[key]] || []
      let value = []
      if (formData.hasOwnProperty(key.replace('.key', '.value'))) {
        value = R.is(Array, formData[key.replace('.key', '.value')]) ? formData[key.replace('.key', '.value')] : [formData[key.replace('.key', '.value')]]
        tagMap[formData[key]].push(...value)
      }
    })
    Object.keys(tagMap).forEach(key => {
      if (tagMap[key].length) {
        ret.push({
          label: `${label}: ${key.replace('user:', '')}=${tagMap[key].map(item => getNameI18n(item, t)).join('|')}`,
        })
      } else {
        ret.push({
          label: `${label}: ${key.replace('user:', '')}`,
        })
      }
    })
  })
  return ret
}

export const getGroupList = (formData) => {
  const ret = []
  const { group = [] } = formData
  group.forEach((g, index) => {
    const l = getGroupName(g)
    ret.push({
      label: `${i18n.t('bill.text_297')}${index + 1}: ${l}`,
    })
  })
  return ret
}
